/**
 * Manages UI component initialization
 *
 * Responsibilities:
 * - Initialize UI components in correct order
 * - Handle component dependencies
 * - Setup component event handlers
 * - Handle initialization errors gracefully
 *
 * Dependencies:
 * - StatusBar: For status display and component containers
 * - LanguageSelector: For document language selection
 * - RecentDocuments: For recent document history
 * - UILanguageButton: For UI language selection
 */

import StatusBar from '../components/status-bar';
import LanguageSelector from '../components/language-selector';
import RecentDocuments from '../components/recent-documents';
import AccountSettings from '../components/account-settings';
import DiscoveryPanel from '../components/discovery-panel';
import ParsingOptionsPanel from '../components/parsing-options-panel';
import ContradictionOffcanvas from '../components/contradiction-offcanvas';
import UILanguageButton from '../i18n/ui-language-button';
import type DocumentHistory from './document-history';
import type FileHandleManager from './file-handle-manager';
import type GatewayService from './gateway-service';
import type DocumentParser from './document-parser';
import type UserManagementService from './user-management-service';
import type Typo3AuthService from './typo3-auth-service';
import type { IUIManager } from '../i18n/ui-manager.types';
import { logger } from './logger';

// Interface for components that can update their UI language
interface LanguageUpdatable {
  initialize(element: HTMLElement): void;
  updateUILanguage(): void;
}

// Configuration for generic component initialization
interface ComponentConfig<T extends LanguageUpdatable> {
  element: HTMLElement | null | undefined;
  ComponentClass: new (options: any) => T;
  options: any;
  componentName: string;
  appPropertyName: keyof App;
  setupLanguageListener?: boolean;
  postInit?: (instance: T) => void;
}

// App interface for ComponentInitializer
export interface App {
  elements: {
    statusBar?: HTMLElement;
    uiLanguageButton?: HTMLElement;
    accountSettings?: HTMLElement;
    discoveryContainer?: HTMLElement;
    parsingOptionsContainer?: HTMLElement;
    contradictionOffcanvas?: HTMLElement;
    /**
     * Module-agnostic hosts for the document stats / language picker
     * (#92 follow-up chrome cleanup). The web app maps these from the
     * Reader sidebar; undefined in Word leaves StatusBar's own footer
     * sections in charge, unchanged.
     */
    documentStats?: HTMLElement;
    documentLanguage?: HTMLElement;
    [key: string]: HTMLElement | undefined;
  };
  uiManager: IUIManager;
  parser: DocumentParser;
  documentHistory: DocumentHistory;
  fileHandleManager: FileHandleManager;
  gatewayService: GatewayService;
  authService?: Typo3AuthService | null;
  userManagementService?: UserManagementService | null;
  handleLanguageChange: (languageCode: string) => void;
  handleAutoDetectToggle: (enabled: boolean) => void;
  handleRecentDocumentSelect: (docMetadata: any) => void;
  handleDocumentListClick?: (doc: any) => void;
  fixUILanguageButtonPositioning?: (() => void) | null;
  createManualUILanguageButton?: (() => void) | null;
  /** Open account settings; the web app routes to #/account (#92). */
  openAccountSettings?: () => void;
  // Component instances (will be set by this initializer)
  statusBar?: StatusBar;
  languageSelector?: LanguageSelector;
  recentDocuments?: RecentDocuments;
  uiLanguageButton?: UILanguageButton;
  accountSettings?: AccountSettings;
  discoveryPanel?: DiscoveryPanel;
  parsingOptionsPanel?: ParsingOptionsPanel;
  contradictionOffcanvas?: ContradictionOffcanvas;
}

class ComponentInitializer {
  private app: App;

  /**
   * @param app - Reference to base application instance
   */
  constructor(app: App) {
    this.app = app;
  }

  /**
   * Generic component initializer for standard components
   * Handles common pattern: check element, create instance, initialize, setup language listener
   * @private
   * @param config - Component configuration
   * @returns The created component instance or null if initialization failed
   */
  private initializeComponent<T extends LanguageUpdatable>(
    config: ComponentConfig<T>
  ): T | null {
    const {
      element,
      ComponentClass,
      options,
      componentName,
      appPropertyName,
      setupLanguageListener = true,
      postInit,
    } = config;

    if (!element) {
      logger.warn(`${componentName} element not found`);
      return null;
    }

    try {
      const instance = new ComponentClass(options);
      instance.initialize(element);

      // Store on app
      (this.app as any)[appPropertyName] = instance;

      logger.debug(`${componentName} initialized successfully`);

      // Setup language change listener if needed
      if (setupLanguageListener) {
        this.app.uiManager.onLanguageChange(() => {
          if ((this.app as any)[appPropertyName]) {
            ((this.app as any)[appPropertyName] as T).updateUILanguage();
          }
        });
      }

      // Run post-init hook if provided
      if (postInit) {
        postInit(instance);
      }

      return instance;
    } catch (error) {
      logger.error(
        `Failed to initialize ${componentName.toLowerCase()}:`,
        error
      );
      return null;
    }
  }

  /**
   * Initialize all UI components in the correct order
   * Order matters: status bar must be initialized before components that use it
   */
  async initializeAll(): Promise<void> {
    this.initializeStatusBar();
    this.initializeUILanguageButton();
    this.initializeLanguageSelector();
    this.initializeRecentDocuments();
    this.initializeAccountSettings();
    this.initializeDiscoveryPanel();
    this.initializeParsingOptionsPanel();
    this.initializeContradictionOffcanvas();
  }

  /**
   * Initialize status bar component
   *
   * The footer (#status-bar) and the module-agnostic external hosts
   * (documentStats/documentLanguage, mapped from the Reader sidebar) are
   * two independent ways to give StatusBar somewhere to render into.
   * Initialize as long as EITHER is present, so the Reader sidebar's stats
   * and language picker keep working once the footer goes away entirely
   * (#92 follow-up: it currently holds only Recent, and disappears once
   * the Files module ships and Recent moves there).
   */
  initializeStatusBar(): void {
    const footerElement = this.app.elements.statusBar;
    const hasExternalHosts = Boolean(
      this.app.elements.documentStats || this.app.elements.documentLanguage
    );

    if (!footerElement && !hasExternalHosts) {
      logger.warn('Status bar element not found');
      return;
    }

    try {
      this.app.statusBar = new StatusBar({
        documentState: 'none',
        cssClass: 'status-bar',
        uiManager: this.app.uiManager, // StatusBar expects a different UIManager type
        // #92 follow-up chrome cleanup: generic pass-through, undefined in Word.
        documentStatsContainer: this.app.elements.documentStats,
        languageContainer: this.app.elements.documentLanguage,
      });

      // StatusBar.render() treats every internal footer section (recent,
      // connection, document) as optional and already skips them when
      // absent. When there is no footer, bind to a detached, inert
      // element purely so BaseComponent.initialize() has a container —
      // the external hosts above are what actually render into.
      this.app.statusBar.initialize(
        footerElement ?? document.createElement('div')
      );

      // Follow the document loading state (UI store)
      this.app.statusBar.subscribeToStore();

      logger.debug('Status bar initialized successfully');

      // Listen for UI language changes to update status bar text
      this.app.uiManager.onLanguageChange(() => {
        this.app.statusBar?.updateUILanguage();
      });
    } catch (error) {
      logger.error('Failed to initialize status bar:', error);
    }
  }

  /**
   * Initialize UI language button with Word-specific fallback handling
   */
  initializeUILanguageButton(): void {
    if (!this.app.elements.uiLanguageButton) {
      logger.warn('UI language button element not found');
      return;
    }

    if (!this.app.uiManager) {
      logger.error(
        'UI Manager not available for UI language button initialization'
      );
      return;
    }

    try {
      this.app.uiLanguageButton = new UILanguageButton({
        uiManager: this.app.uiManager, // UILanguageButton expects a different UIManager type
        cssClass: 'ui-language-button',
      });

      this.app.uiLanguageButton.initialize(this.app.elements.uiLanguageButton);
      logger.debug('UI language button initialized successfully');

      // Apply environment-specific fixes if needed
      if (this.app.fixUILanguageButtonPositioning) {
        this.app.fixUILanguageButtonPositioning();
      }

      // Test if dropdown works, fallback to manual if needed
      setTimeout(() => {
        const dropdown = document.querySelector('.ui-language-dropdown');
        if (!dropdown && this.app.createManualUILanguageButton) {
          logger.warn(
            'UILanguageButton dropdown not created, switching to manual implementation'
          );
          this.app.createManualUILanguageButton();
        }
      }, 200);
    } catch (error) {
      logger.error('Failed to initialize UI language button:', error);

      if (this.app.createManualUILanguageButton) {
        this.app.createManualUILanguageButton();
      }
    }
  }

  /**
   * Initialize document language selector
   */
  initializeLanguageSelector(): void {
    // Check if status bar is available for compact mode
    const languageContainer = this.app.statusBar?.getLanguageContainer();
    if (!this.app.statusBar || !languageContainer) {
      logger.warn('Status bar not available for language selector');
      return;
    }

    const languageOptions = this.app.parser.getLanguageOptions();

    this.app.languageSelector = new LanguageSelector({
      currentLanguage: '',
      languages: languageOptions,
      autoDetected: true,
      showAutoDetect: true,
      cssClass: 'language-selector language-selector--web',
      compact: true, // Use compact mode for status bar
      // The selector lives in the reader sidebar, so stack it (#92).
      stacked: true,
      onLanguageChange: this.app.handleLanguageChange.bind(this.app),
      onAutoDetectToggle: this.app.handleAutoDetectToggle.bind(this.app),
      uiManager: this.app.uiManager,
      hasDocument: false,
    });

    // Initialize in status bar language container
    this.app.languageSelector.initialize(languageContainer);

    // Listen for UI language changes
    this.app.uiManager.onLanguageChange(() => {
      this.app.languageSelector?.updateUILanguage();
    });

    // Ensure parser auto-detect setting matches the language selector default
    this.app.parser.setAutoDetectLanguage(
      this.app.languageSelector.isAutoDetectEnabled()
    );

    logger.debug('Language selector initialized in status bar (compact mode)');
  }

  /**
   * Initialize recent documents component
   */
  initializeRecentDocuments(): void {
    const container = this.app.statusBar?.getRecentContainer();
    if (!container) {
      logger.warn('Recent documents container not found');
      return;
    }

    this.app.recentDocuments = new RecentDocuments({
      documentHistory: this.app.documentHistory,
      uiManager: this.app.uiManager,
      fileHandleManager: this.app.fileHandleManager,
      onDocumentSelect: this.app.handleRecentDocumentSelect.bind(this.app),
    });

    this.app.recentDocuments.initialize(container);
    this.app.statusBar?.setRecentDocuments(this.app.recentDocuments);

    logger.debug('Recent documents initialized in status bar');
  }

  /**
   * Initialize account settings
   */
  initializeAccountSettings(): void {
    this.initializeComponent<AccountSettings>({
      element: this.app.elements.accountSettings,
      ComponentClass: AccountSettings,
      options: {
        cssClass: 'account-settings',
        uiManager: this.app.uiManager,
        userService: this.app.userManagementService || undefined,
      },
      componentName: 'Account settings',
      appPropertyName: 'accountSettings',
    });
  }

  /**
   * Initialize discovery panel component (issue #61)
   */
  initializeDiscoveryPanel(): void {
    this.initializeComponent<DiscoveryPanel>({
      element: this.app.elements.discoveryContainer,
      ComponentClass: DiscoveryPanel,
      options: {
        gatewayService: this.app.gatewayService,
        cssClass: 'discovery-panel',
        uiManager: this.app.uiManager,
        onDocumentClick: this.app.handleDocumentListClick?.bind(this.app),
      },
      componentName: 'Discovery panel',
      appPropertyName: 'discoveryPanel',
    });
  }

  /**
   * Initialize parsing options panel component (issue #57)
   */
  initializeParsingOptionsPanel(): void {
    this.initializeComponent<ParsingOptionsPanel>({
      element: this.app.elements.parsingOptionsContainer,
      ComponentClass: ParsingOptionsPanel,
      options: {
        cssClass: 'parsing-options-panel',
        uiManager: this.app.uiManager,
      },
      componentName: 'Parsing options panel',
      appPropertyName: 'parsingOptionsPanel',
    });
  }

  /**
   * Initialize contradiction offcanvas component (issue #66)
   */
  initializeContradictionOffcanvas(): void {
    this.initializeComponent<ContradictionOffcanvas>({
      element: this.app.elements.contradictionOffcanvas,
      ComponentClass: ContradictionOffcanvas,
      options: {
        cssClass: 'contradiction-offcanvas',
        uiManager: this.app.uiManager,
      },
      componentName: 'Contradiction offcanvas',
      appPropertyName: 'contradictionOffcanvas',
    });
  }

  /**
   * Update account settings with user service after it's available
   */
  updateAccountSettingsUserService(userService: UserManagementService): void {
    if (this.app.accountSettings) {
      this.app.accountSettings.setUserService(userService);
    }
  }
}

export default ComponentInitializer;
